import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import dayjs from 'dayjs';
import { GraphService } from './GraphService';
import { MsalService } from './MsalService';
import * as Bixby from '$/core';

export abstract class PlannerHealthService extends GraphService {

  private static baseUrl = `${Bixby.Constants.API_ADMIN_PREFIX}/${Bixby.Constants.API_PLANNERCONTROLLER}`;

  private static delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  private static delayRandom = (maxInSeconds) => PlannerHealthService.delay(Math.floor(Math.random() * maxInSeconds * 1000));

  public static async getPlannerHealth(): Promise<Bixby.Models.PlannerHealthResult> {
    const url = `${PlannerHealthService.baseUrl}/${Bixby.Constants.API_ACTION_HEALTH}`;
    const route = Bixby.API.ServerRoute.forAction(url);
    return await Bixby.API.get(route);
  }

  public static async cachePlannerHealth(results: Bixby.Models.PlannerHealthResult): Promise<void> {
    const route = Bixby.API.ServerRoute.forAction(`${PlannerHealthService.baseUrl}/${Bixby.Constants.API_ACTION_HEALTH}`);
    return await Bixby.API.post(route, results);
  }

  public static async analyzePlannerDataAsync(): Promise<Bixby.Models.PlannerHealthResult> {
    const planResults: Bixby.Models.PlanResult[] = [];
    const userResults: Bixby.Models.UserResult[] = [];
    const userTaskCounts: { [key: string]: number } = {};
    const userActiveTaskCounts: { [key: string]: number } = {};

    const plans = await PlannerHealthService.getAllPlansForGroupAsync();

    for (const plan of plans) {
      if (plan.id && plan.title) {

        const bucketTaskCounts: { [key: string]: number } = {};
        const bucketActiveTaskCounts: { [key: string]: number } = {};

        // Only include plans with buckets. All tasks must be in a bucket.
        const buckets = await PlannerHealthService.getBucketsForPlanAsync(plan.id);
        if (buckets.length === 0) {
          continue;
        }

        const planTasks = await PlannerHealthService.getTasksForPlanAsync(plan.id);
        const activeTasks = planTasks.filter(t => t.completedDateTime == null && t.percentComplete != 100);

        for (const task of planTasks) {
          if (task.bucketId) {
            const isActive = task.completedDateTime == null && task.percentComplete != 100;

            if (bucketTaskCounts[task.bucketId]) {
              bucketTaskCounts[task.bucketId]++;
            } else {
              bucketTaskCounts[task.bucketId] = 1;
            }

            if (isActive) {
              if (bucketActiveTaskCounts[task.bucketId]) {
                bucketActiveTaskCounts[task.bucketId]++;
              } else {
                bucketActiveTaskCounts[task.bucketId] = 1;
              }
            }

            if (task.assignments) {
              for (const userId of Object.keys(task.assignments)) {
                if (userTaskCounts[userId]) {
                  userTaskCounts[userId]++;
                } else {
                  userTaskCounts[userId] = 1;
                }

                if (isActive) {
                  if (userActiveTaskCounts[userId]) {
                    userActiveTaskCounts[userId]++;
                  } else {
                    userActiveTaskCounts[userId] = 1;
                  }
                }
              }
            }
          }
        }

        const bucketResults: Bixby.Models.BucketResult[] = [];
        const sortedBucketTaskCounts = Object.entries(bucketTaskCounts).sort((a, b) => b[1] - a[1]);

        for (const [bucketId, totalTaskCount] of sortedBucketTaskCounts) {
          const bucketName = buckets.find(b => b.id === bucketId)?.name;
          if (!bucketName) {
            throw new Error('Bucket not found');
          }

          const totalActiveTaskCount = bucketActiveTaskCounts[bucketId] || 0;
          bucketResults.push({
            bucketId,
            bucketName,
            totalTaskCount,
            totalActiveTaskCount
          });
        }

        planResults.push({
          planId: plan.id,
          planTitle: plan.title,
          totalTaskCount: planTasks.length,
          totalActiveTaskCount: activeTasks.length,
          buckets: bucketResults
        });
      }

      await PlannerHealthService.delayRandom(3);
    }

    const sortedUserTaskCounts = Object.entries(userTaskCounts).sort((a, b) => b[1] - a[1]);

    for (const [userId, totalTaskCount] of sortedUserTaskCounts) {
      const displayName = await PlannerHealthService.getUserDisplayNameAsync(userId);
      const totalActiveTaskCount = userActiveTaskCounts[userId] || 0;
      userResults.push({
        displayName,
        totalTaskCount,
        totalActiveTaskCount
      });
    }

    const results = {
      lastRun: dayjs.utc().toDate(),
      planResults,
      userResults
    };

    await PlannerHealthService.cachePlannerHealth(results);

    return results;
  }

  public static async getBucketsForPlanAsync(planId: string): Promise<MicrosoftGraph.PlannerBucket[]> {
    const token = await MsalService.getPlannerToken();
    if (!token) {
      throw new Error('Token not found');
    }

    const bucketsResponse = await PlannerHealthService.get<{ value: MicrosoftGraph.PlannerBucket[] }>(
      token,
      `/planner/plans/${planId}/buckets`
    );

    return bucketsResponse.value || [];
  }

  private static async getAllPlansForGroupAsync(): Promise<MicrosoftGraph.PlannerPlan[]> {
    const groupId = Bixby.Configuration.plannerDataSiteGroupId;
    const plans: MicrosoftGraph.PlannerPlan[] = [];

    const token = await MsalService.getPlannerToken();
    if (!token) {
      throw new Error('Token not found');
    }

    const group = await PlannerHealthService.get<MicrosoftGraph.Group>(
      token,
      `/groups/${groupId}?$select=id,displayName`
    );

    const groupPlans = await PlannerHealthService.get<{ value: MicrosoftGraph.PlannerPlan[] }>(
      token,
      `/groups/${group.id}/planner/plans`
    );

    if (groupPlans.value) {
      plans.push(...groupPlans.value);
    }

    return plans;
  }

  private static async getTasksForPlanAsync(planId: string): Promise<MicrosoftGraph.PlannerTask[]> {
    const tasks: MicrosoftGraph.PlannerTask[] = [];

    const token = await MsalService.getPlannerToken();
    if (!token) {
      throw new Error('Token not found');
    }

    let plannerTasks = await PlannerHealthService.get<{ value: MicrosoftGraph.PlannerTask[]; '@odata.nextLink'?: string }>(
      token,
      `/planner/plans/${planId}/tasks`
    );

    while (plannerTasks.value) {
      tasks.push(...plannerTasks.value);

      await PlannerHealthService.delayRandom(3);

      if (plannerTasks['@odata.nextLink']) {
        const nextUrl = new URL(plannerTasks['@odata.nextLink']);
        plannerTasks = await PlannerHealthService.get<{ value: MicrosoftGraph.PlannerTask[]; '@odata.nextLink'?: string }>(
          token,
          `/planner/plans/${planId}/tasks${nextUrl.search}`
        );
      } else {
        break;
      }
    }

    return tasks;
  }

  private static async getUserDisplayNameAsync(userId: string): Promise<string> {
    const token = await MsalService.getPlannerToken();
    if (!token) {
      return userId;
    }

    const user = await PlannerHealthService.get<MicrosoftGraph.User>(token, `/users/${userId}`);
    return user.displayName || user.userPrincipalName || userId;
  }
}
